import styled from "styled-components";
import { useState } from "react";
import { quoteApi } from "@api";
import { ErrorToast } from "src/Components/toasts/ErrorToast";
import { breakpoint, theme } from "@utilities";

interface CreateNewTravelerModalProps {
    quoteId: string;
    onClose: () => void;
    onTravelerAdded: () => void;
}

export const CreateNewTravelerModal = ({
    quoteId,
    onClose,
    onTravelerAdded,
}: CreateNewTravelerModalProps) => {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emailError, setEmailError] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim() || !email.trim()) {
            ErrorToast({ Message: "Please fill in all fields" });
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setEmailError("Please enter a valid email address");
            return;
        }

        try {
            setIsSubmitting(true);
            await quoteApi.addPaymentReceiver({
                quoteId,
                email: email.trim(),
                name: name.trim(),
                redeemerId: null,
            });
            onTravelerAdded();
            handleClose();
        } catch (error: any) {
            console.error(error);
            ErrorToast({ Message: "Failed to add traveler" });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        if (emailError && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value.trim())) {
            setEmailError("");
        }
    };

    const handleClose = () => {
        setName("");
        setEmail("");
        setEmailError("");
        onClose();
    };

    return (
        <>
            <Overlay onClick={handleClose} />
            <Modal onClick={(e) => e.stopPropagation()}>
                <CloseButton onClick={handleClose}>
                    <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#080914"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </CloseButton>
                <ModalContent>
                    <Title>Add Email</Title>
                    <Subtitle>Add Email address to send the payment link</Subtitle>
                    <FormContainer onSubmit={handleSubmit}>
                        <FormFields>
                            <FormField>
                                <Label>Name</Label>
                                <InputContainer>
                                    <Input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Name"
                                        required
                                    />
                                </InputContainer>
                            </FormField>
                            <FormField>
                                <Label>Email</Label>
                                <InputContainer>
                                    <Input
                                        type="email"
                                        value={email}
                                        onChange={handleEmailChange}
                                        placeholder="Email"
                                        required
                                    />
                                </InputContainer>
                                {emailError && <ErrorMessage>{emailError}</ErrorMessage>}
                            </FormField>
                        </FormFields>
                        <SaveButton type="submit" disabled={isSubmitting || !!emailError}>
                            {isSubmitting ? "Saving..." : "Save"}
                        </SaveButton>
                    </FormContainer>
                </ModalContent>
            </Modal>
        </>
    );
};

const CloseButton = styled.button`
    position: absolute;
    top: 33px;
    right: 33px;
    width: 24px;
    height: 24px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    width: 24px;
    height: 24px;

    @media (max-width: 768px) {
        min-width: 44px;
        -webkit-tap-highlight-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
    }

    ${breakpoint.breakTablet`
        transition: opacity 0.2s ease;
        &:hover {
            opacity: 0.7;
        }
    `}
`;

const Overlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 20;
`;

const Modal = styled.div`
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: #ffffff;
    border-radius: 10px;
    width: 90%;
    max-width: 732px;
    z-index: 21;
    padding: 38px;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
    max-width: 656px;
    margin: 0 auto;
`;

const Title = styled.h1`
    margin: 0;
    font-family: "Poppins", sans-serif;
    font-weight: 600;
    font-size: 28px;
    line-height: 140%;
    color: #000000;
    align-self: stretch;
`;

const Subtitle = styled.p`
    margin: 0;
    font-family: "Poppins", sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 120%;
    color: #000000;
    align-self: stretch;
`;

const FormContainer = styled.form`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 30px;
    align-self: stretch;
`;

const FormFields = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    align-self: stretch;
`;

const FormField = styled.div`
    position: relative;
    align-self: stretch;
`;

const InputContainer = styled.div`
    box-sizing: border-box;
    height: 45px;
    border-bottom: 1px solid #c5c7c9;
    position: relative;
    align-self: stretch;
`;

const Label = styled.label`
    position: absolute;
    top: 1px;
    left: 0;
    font-family: "Poppins", sans-serif;
    font-weight: 500;
    font-size: 12px;
    line-height: 26px;
    letter-spacing: 0.15px;
    color: ${theme.colors.time_radio};
`;

const Input = styled.input`
    padding-top: 8px;
    position: absolute;
    top: 17px;
    left: 0;
    width: 100%;
    border: none;
    background: transparent;
    font-weight: 400;
    font-size: 16px;
    line-height: 120%;
    color: #131214;

    &::placeholder {
        color: #898d8f;
        transition: opacity 0.2s ease;
    }

    &:focus {
        outline: none;
    }

    &:focus::placeholder {
        opacity: 0;
    }
`;

const ErrorMessage = styled.p`
    margin: 4px 0 0 0;
    font-family: "Poppins", sans-serif;
    font-weight: 400;
    font-size: 12px;
    color: #ff0000;
`;

const SaveButton = styled.button`
    width: 216px;
    height: 54px;
    background: linear-gradient(
        to right,
        ${theme.gradients.button[0]},
        ${theme.gradients.button[1]}
    );
    border: none;
    border-radius: 4px;
    cursor: pointer;
    position: relative;

    font-family: "Poppins", sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 26px;
    color: ${theme.colors.button_text};
    text-align: center;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;
